import React, { useEffect, useState } from 'react';
import
{
    ArrowRight,
    CalendarPlusFill,
    GearFill,
    LightningChargeFill,
    StarFill
} from 'react-bootstrap-icons';
import { getReleaseAt, getReleaseDisplayLabel } from '../utils/releaseGate';
import './CountdownAcceliqa.css';

const MARQUEE_ITEMS = [
    'AI test cases',
    'Self-healing runs',
    'Gherkin suites',
    'Data validation',
    'Synthetic data',
    'Release evidence',
    'Sentinel reviews'
];

interface CountdownParts
{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
    reached: boolean;
}

function getCountdownParts(target: Date | null): CountdownParts
{
    if (!target)
    {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, reached: false };
    }

    const remainingMs = Math.max(0, target.getTime() - Date.now());
    const totalSeconds = Math.floor(remainingMs / 1000);

    return {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
        reached: remainingMs <= 0
    };
}

function openCalendarEvent(releaseAt: Date): void
{
    const stamp = (date: Date): string => date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    const end = new Date(releaseAt.getTime() + 3600000);
    const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: 'AcceliQA Launch',
        dates: `${stamp(releaseAt)}/${stamp(end)}`,
        details: 'AcceliQA goes live. Your AI QA team reports for duty. https://acceliqa.accelirate.com',
        location: 'https://acceliqa.accelirate.com'
    });
    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank', 'noopener');
}

interface TimerUnitProps
{
    value: number;
    label: string;
    tone: string;
}

function TimerUnit({ value, label, tone }: TimerUnitProps): React.ReactElement
{
    const display = String(value).padStart(2, '0');
    return (
        <div className={`cs-unit cs-unit-${tone}`}>
            <div className="cs-unit-card">
                <span key={display} className="cs-unit-value">{display}</span>
            </div>
            <span className="cs-unit-label">{label}</span>
        </div>
    );
}

function CountdownAcceliqa(): React.ReactElement
{
    const releaseAt = getReleaseAt();
    const [countdown, setCountdown] = useState<CountdownParts>(() => getCountdownParts(releaseAt));
    const [videoFailed, setVideoFailed] = useState(false);

    useEffect(() =>
    {
        setCountdown(getCountdownParts(releaseAt));
        const timer = window.setInterval(() =>
        {
            setCountdown(getCountdownParts(releaseAt));
        }, 1000);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [releaseAt?.getTime()]);

    const isLive = countdown.reached && releaseAt !== null;

    return (
        <div className="cs-root">
            <div className="cs-doodle cs-doodle-star" aria-hidden="true"><StarFill /></div>
            <div className="cs-doodle cs-doodle-gear" aria-hidden="true"><GearFill /></div>
            <div className="cs-doodle cs-doodle-bolt" aria-hidden="true"><LightningChargeFill /></div>

            <header className="cs-topbar">
                <div className="cs-brand">
                    <img src="/Accelirate.svg" alt="" />
                    <span>Acceli<em>QA</em></span>
                </div>
                <div className="cs-sticker">Coming soon</div>
            </header>

            {isLive ? (
                <main className="cs-main cs-live">
                    <div className="cs-confetti" aria-hidden="true">
                        {Array.from({ length: 24 }, (_, index) => (
                            <span
                                key={index}
                                className={`cs-confetti-piece cs-cp-${index % 6}`}
                                style={{ left: `${(index * 41) % 100}%`, animationDelay: `${(index % 8) * 0.4}s` }}
                            />
                        ))}
                    </div>
                    <h1>We&apos;re live!</h1>
                    <p>AcceliQA has launched. Your AI QA team reports for duty.</p>
                    <button className="cs-btn" type="button" onClick={() => window.location.assign('/')}>
                        Enter AcceliQA
                        <ArrowRight />
                    </button>
                </main>
            ) : (
                <main className="cs-main">
                    <h1 className="cs-title">
                        Something <span className="cs-title-big">BIG</span> is coming
                    </h1>
                    <p className="cs-subtitle">
                        Your AI QA team is doing the final checks. The site opens automatically
                        when the countdown ends.
                    </p>

                    <div className="cs-video-card">
                        {videoFailed ? (
                            <img
                                className="cs-video-fallback"
                                src="/landing/mascot.png"
                                alt="AcceliQA mascot"
                                draggable={false}
                            />
                        ) : (
                            <video
                                className="cs-video"
                                src="/landing/coming-soon-video.mp4"
                                autoPlay
                                muted
                                loop
                                playsInline
                                onError={() => setVideoFailed(true)}
                            />
                        )}
                    </div>

                    <div className="cs-timer" role="timer" aria-label="Countdown to launch">
                        <TimerUnit value={countdown.days} label="Days" tone="blue" />
                        <TimerUnit value={countdown.hours} label="Hours" tone="yellow" />
                        <TimerUnit value={countdown.minutes} label="Minutes" tone="mint" />
                        <TimerUnit value={countdown.seconds} label="Seconds" tone="coral" />
                    </div>

                    <div className="cs-date">
                        <StarFill />
                        <span>{getReleaseDisplayLabel()}</span>
                    </div>

                    {releaseAt && (
                        <button className="cs-btn" type="button" onClick={() => openCalendarEvent(releaseAt)}>
                            <CalendarPlusFill />
                            Add to calendar
                        </button>
                    )}
                </main>
            )}

            <div className="cs-flyer" aria-hidden="true">
                <span className="cs-flyer-trail" />
                <img
                    src="/landing/mascot-flying.png"
                    alt=""
                    draggable={false}
                    onError={(event) =>
                    {
                        const flyer = event.currentTarget.parentElement;
                        if (flyer)
                        {
                            flyer.style.display = 'none';
                        }
                    }}
                />
            </div>

            <footer className="cs-marquee" aria-hidden="true">
                <div className="cs-marquee-track">
                    {[...Array(2)].map((_, copy) => (
                        <div key={copy} className="cs-marquee-group">
                            {MARQUEE_ITEMS.map((item) => (
                                <span key={item}>
                                    <StarFill />
                                    {item}
                                </span>
                            ))}
                        </div>
                    ))}
                </div>
            </footer>
        </div>
    );
}

export default CountdownAcceliqa;
